import { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from './useUser';

/**
 * Hook for user-specific localStorage storage
 * Automatically prefixes keys with user ID to isolate data per user
 * Falls back to global storage when user is not logged in
 */
export function useUserLocalStorage(key, initialValue) {
  const { currentUser } = useUser();
  const isBrowser = typeof window !== 'undefined';
  const initialValueRef = useRef(initialValue);

  // Update the ref when initialValue changes, but don't trigger effects
  initialValueRef.current = initialValue;

  const [storedValue, setStoredValue] = useState(() => {
    try {
      if (!isBrowser) {
        return initialValue;
      }
      const storageKey = currentUser?.id ? `user_${currentUser.id}_${key}` : `global_${key}`;
      const item = window.localStorage.getItem(storageKey);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key:`, error);
      return initialValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      // Compute current storage key inside setValue to avoid stale closure
      const currentStorageKey = currentUser?.id ? `user_${currentUser.id}_${key}` : `global_${key}`;

      setStoredValue((prevValue) => {
        const valueToStore = value instanceof Function ? value(prevValue) : value;
        if (isBrowser) {
          window.localStorage.setItem(currentStorageKey, JSON.stringify(valueToStore));
        }
        return valueToStore;
      });
    } catch (error) {
      console.error(`Error setting localStorage key:`, error);
    }
  }, [currentUser?.id, key, isBrowser]);

  // Update storage when user changes (login/logout)
  useEffect(() => {
    // Compute current storage key inside effect
    const currentStorageKey = currentUser?.id ? `user_${currentUser.id}_${key}` : `global_${key}`;

    if (!isBrowser) {
      setStoredValue(initialValueRef.current);
      return;
    }

    try {
      const item = window.localStorage.getItem(currentStorageKey);
      const newValue = item ? JSON.parse(item) : initialValueRef.current;
      setStoredValue(newValue);
    } catch (error) {
      console.error(`Error reading localStorage key "${currentStorageKey}":`, error);
      setStoredValue(initialValueRef.current);
    }
  }, [currentUser?.id, key, isBrowser]);

  return [storedValue, setValue];
}

/**
 * Utility function to migrate existing global data to user-specific storage
 * Call this once when user logs in to preserve their existing data
 */
export function migrateGlobalToUserStorage(userId, key) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const globalKey = `global_${key}`;
    const userKey = `user_${userId}_${key}`;

    const globalData = window.localStorage.getItem(globalKey);
    if (globalData && !window.localStorage.getItem(userKey)) {
      window.localStorage.setItem(userKey, globalData);
      console.log(`Migrated ${key} from global to user storage`);
    }
  } catch (error) {
    console.error(`Error migrating ${key} to user storage:`, error);
  }
}

/**
 * Utility function to clear all data for a specific user
 * Useful for logout cleanup
 */
export function clearUserStorage(userId) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const keysToRemove = [];
    const prefix = `user_${userId}_`;

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => window.localStorage.removeItem(key));
    console.log(`Cleared ${keysToRemove.length} user storage items`);
  } catch (error) {
    console.error('Error clearing user storage:', error);
  }
}